import { useEffect, useState } from "react";
import { format } from "date-fns";
import { RefreshCw } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { DATE_LOCALES, useI18n } from "@/lib/i18n";

export const calClassNames = {
  months: "flex flex-col sm:flex-row gap-8",
  head_cell: "text-muted2 w-10 sm:w-11 font-medium text-[0.75rem]",
  cell: "relative p-0 text-center text-sm focus-within:z-20 [&:has([aria-selected].day-range-middle)]:bg-[#F0E4D3] [&:has(.day-range-start)]:rounded-l-full [&:has(.day-range-end)]:rounded-r-full [&:has(.day-range-start)]:bg-[#F0E4D3] [&:has(.day-range-end)]:bg-[#F0E4D3]",
  day: "h-10 w-10 sm:h-11 sm:w-11 rounded-full p-0 font-medium text-ink hover:ring-1 hover:ring-ink aria-selected:opacity-100 transition-shadow",
  day_selected: "bg-ink text-paper hover:bg-ink focus:bg-ink",
  day_range_middle: "aria-selected:bg-transparent aria-selected:text-ink",
  day_today: "underline underline-offset-4",
  day_disabled: "text-ink/30 line-through hover:ring-0 cursor-not-allowed",
  caption_label: "font-display text-xl text-ink",
};

export const useIsWide = (px = 768) => {
  const [w, setW] = useState(() => window.innerWidth >= px);
  useEffect(() => {
    const on = () => setW(window.innerWidth >= px);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [px]);
  return w;
};

export const BookingCalendar = ({ b, months }) => {
  const { lang } = useI18n();
  return (
    <Calendar
      mode="range"
      numberOfMonths={months}
      selected={b.range}
      onSelect={b.onSelect}
      disabled={b.disabled}
      locale={DATE_LOCALES[lang]}
      showOutsideDays={false}
      fromDate={new Date()}
      classNames={calClassNames}
      className="p-0"
    />
  );
};

export const DatesSection = ({ b, synced }) => {
  const { t, lang } = useI18n();
  const wide = useIsWide();
  const L = DATE_LOCALES[lang];
  const f = (d) => format(d, "d MMM yyyy", { locale: L });
  const title = b.nights ? (b.nights === 1 ? t("cal.night1") : t("cal.nights", { n: b.nights })) : b.range.from ? t("cal.pickOut") : t("cal.pick");
  return (
    <section id="dates" className="border-t border-ink/10 pt-10" data-testid="availability-section">
      <h3 data-testid="dates-title" className="font-display text-3xl text-ink">{title}</h3>
      <p className="mt-1 text-sm text-muted2">{b.range.from ? `${f(b.range.from)}${b.range.to ? ` – ${f(b.range.to)}` : ""}` : b.minNights > 1 ? t("w.min", { n: b.minNights }) : "\u00a0"}</p>
      <div className="mt-6 overflow-x-auto" data-testid="inline-calendar"><BookingCalendar b={b} months={wide ? 2 : 1} /></div>
      <div className="mt-4 flex items-center justify-between gap-4">
        {synced ? <span className="flex items-center gap-2 text-xs text-forest"><RefreshCw className="h-3.5 w-3.5" /> {t("cal.synced")}</span> : <span />}
        <button data-testid="clear-dates-button" onClick={b.clear} className="text-sm font-semibold underline underline-offset-4">{t("cal.clear")}</button>
      </div>
    </section>
  );
};
